/* eslint no-underscore-dangle: ["error", { "allow": ["_history"]},] */
import { Box, Skeleton } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridComparatorFn,
  GridRenderCellParams,
  GridRowModel,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import React, { useCallback, useMemo } from "react";

import { BOUNDING_BOX, GREATER_THAN_MAX, labelInternalToUI, labelMap, NO_OBJECTS, sourceMappingsIQ } from "config/LabelMapping";
import { useAppSelector } from "hooks/store";
import { renderWithTooltip } from "pages/data/components/dataGrid/columnsComponents/columnsComponents";
import { getUserState } from "store/slices/Auth/authSlice";
import { DetectorModes } from "types/createDetector";
import { Detector } from "types/detectors";
import { REVIEW_REASON_CHOICES } from "types/imageQueries";
import { CountAnswer, GeneralAnswer, LabelValue, TextAnswer } from "types/labelValue";
import { convertUTCtoLocalReadableDate, parseDateAsUTC } from "utils/utilityFunctions";

export interface LabelTableProps {
  loading: boolean;
  labelRows: LabelValue[];
  detector: Detector;
  onLabelSelected: (labelId: number) => void;
  selectedLabelId?: number | null;
  bestPredictionLabelId?: number | null;
}

// Define types for the hidden columns and answer formatting functions to improve type safety
type HiddenColumnsMap = { [key in DetectorModes]: string[] };

const HIDDEN_COLUMNS_BY_MODE: HiddenColumnsMap = {
  [DetectorModes.BINARY]: [],
  [DetectorModes.MULTICHOICE]: [],
  [DetectorModes.COUNT]: [],
  [DetectorModes.TEXT_RECOGNITION]: [],
  [DetectorModes.BOUNDING_BOX]: [],
};

/**
 * LabelTable component displays label data in a grid based on detector mode
 * and user permissions
 */
export function LabelTable({
  loading,
  labelRows,
  detector,
  bestPredictionLabelId,
  selectedLabelId,
  onLabelSelected,
}: LabelTableProps): JSX.Element {
  const userData = useAppSelector(getUserState);

  // Date comparison function for sorting dates
  const dateComparator: GridComparatorFn<Date> = (v1, v2) => {
    // Handle cases where v1 or v2 might be undefined
    if (!v1 && !v2) return 0;
    if (!v1) return -1;
    if (!v2) return 1;

    // Both values are valid dates, compare them
    return v1.getTime() - v2.getTime();
  };

  /**
   * Formats the answer value based on detector mode and answer type
   * @param params Grid value getter params containing row data
   * @returns Formatted answer value
   */
  const getAnswerLabel = useCallback((params: GridValueGetterParams): string | null => {
    const row = params.row as LabelValue;
    let answer: string | CountAnswer | GeneralAnswer | TextAnswer = row.answer as string;

    // Handling __UNSURE for all modes
    if (row.class_name === "__UNSURE") return "Unclear";

    // Handle different detector modes
    switch (detector.mode) {
      case DetectorModes.BINARY:
        answer = row.answer as string;
        return labelMap.get(row.class_name) ?? null;

      case DetectorModes.TEXT_RECOGNITION:
        answer = row.answer as TextAnswer;
        if (answer.value === "" || answer.value === null) return "----";
        return answer.value;

      case DetectorModes.COUNT:
        answer = row.answer as CountAnswer;
        if (answer.answer_type === GREATER_THAN_MAX) {
          // In the backend as label value, the max count is represented as detector.mode_configuration?.max_count + 1
          // So we need to subtract 1 to get the actual max count
          return `>${detector.mode_configuration?.max_count ?? (answer.value - 1)}`;
        }
        return String(answer.value);

      case DetectorModes.BOUNDING_BOX:
        answer = row.answer as GeneralAnswer;
        if (answer.value === GREATER_THAN_MAX) {
          return `>${detector.mode_configuration?.max_num_bboxes ?? 0} boxes`;
        }
        if (answer.value === NO_OBJECTS) {
          return "No objects";
        }
        if (answer.value === BOUNDING_BOX) {
          return "Bounding boxes";
        }
        return String(answer.value);

      default:
        answer = row.answer as GeneralAnswer;
        return answer.value;
    }
  }, [detector]);

  /**
   * Creates date cell renderer
   * @param params Grid render cell params
   * @returns Rendered date cell
   */
  const renderDateCell = (params: GridRenderCellParams) => {
    const date = params.row.created_at;
    return <div>{convertUTCtoLocalReadableDate(date)}</div>;
  };

  /**
   * Creates labeled by cell renderer
   * @param params Grid render cell params
   * @returns Rendered labeled by cell
   */
  const renderLabeledByCell = (params: GridRenderCellParams) => {
    const { company, created_by_id: createdBy } = params.row as LabelValue;
    return (
      <div>
        {createdBy || "--"} {company && `- ${company}`}
      </div>
    );
  };

  /**
   * Creates review reason cell renderer
   * @param params Grid render cell params
   * @returns Rendered review reason cell
   */
  const renderReviewReasonCell = (params: GridRenderCellParams) => {
    const { review_reason: reviewReason } = params.row as LabelValue;
    return reviewReason ? (
      <p className="capitalize">{reviewReason.toLowerCase()}</p>
    ) : (
      <p>-</p>
    );
  };

  // Define base columns available to all users
  const baseColumns: GridColDef[] = useMemo(() => [
    {
      field: "answer_type",
      headerName: "",
      type: "string",
      flex: 2,
      align: "center",
      headerAlign: "center",
      width: 60,
      minWidth: 60,
      valueGetter: (params) => {
        const isBestAnswer = bestPredictionLabelId !== undefined ? params.row.id === bestPredictionLabelId : false;
        if (isBestAnswer) {
          return "→";
        }
        return "";
      },
    },
    {
      field: "time",
      headerName: "Time",
      type: "date",
      flex: 2,
      align: "center",
      headerAlign: "center",
      width: 200,
      minWidth: 200,
      sortComparator: dateComparator,
      valueGetter: (params: GridValueGetterParams) => parseDateAsUTC(params.row.created_at),
      renderCell: renderDateCell,
    },
    {
      field: "answer",
      headerName: "Answer",
      width: 90,
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 100,
      valueGetter: getAnswerLabel,
    },
    {
      field: "source",
      headerName: "Source of answer",
      align: "center",
      headerAlign: "center",
      flex: 2,
      width: 200,
      minWidth: 180,
      cellClassName: "p-0",
      renderCell: (params) =>
        renderWithTooltip(labelInternalToUI(sourceMappingsIQ, (params.row as LabelValue).source) || "--"),
    },
    {
      field: "confidence",
      headerName: "Confidence",
      align: "center",
      headerAlign: "center",
      type: "number",
      flex: 1,
      width: 100,
      minWidth: 100,
      valueGetter: (params: GridValueGetterParams) => (params.row as LabelValue).confidence ?? "--",
    },
    {
      field: "is_audited",
      headerName: "Audited (GL)",
      align: "center",
      headerAlign: "center",
      width: 150,
      flex: 1,
      minWidth: 100,
      valueGetter: (params: GridValueGetterParams) =>
        (params.row as LabelValue).review_reason === REVIEW_REASON_CHOICES.AUDIT,
    },
    {
      field: "feedback_text",
      headerName: "Feedback Text",
      align: "center",
      headerAlign: "center",
      width: 150,
      flex: 2,
      minWidth: 140,
      valueGetter: (params: GridValueGetterParams) => (params.row as LabelValue).feedback_text || "--",
    },
  ], [getAnswerLabel, bestPredictionLabelId]);

  // Define additional columns available only to admin users
  const adminColumns: GridColDef[] = useMemo(() => [
    {
      field: "labeled_by",
      headerName: "Labeled by",
      type: "number",
      align: "center",
      headerAlign: "center",
      flex: 1,
      width: 90,
      minWidth: 100,
      renderCell: renderLabeledByCell,
    },
    {
      field: "review_reason",
      headerName: "Review reason",
      align: "center",
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
      renderCell: renderReviewReasonCell,
    },
  ], []);

  // Filter columns based on detector mode and user role
  const columns: GridColDef[] = useMemo(() => {
    // Get columns based on user role
    const allColumns = userData.is_superuser
      ? [...baseColumns, ...adminColumns]
      : baseColumns;

    return allColumns.map(column => ({
      ...column,
      // User cannot sort
      sortable: false
    }))
      // Filter out hidden columns based on detector mode
      .filter(
        (column) => !HIDDEN_COLUMNS_BY_MODE[detector.mode].includes(column.field)
      );
  }, [userData, baseColumns, adminColumns, detector]);

  // Show loading skeleton when loading data
  if (loading) {
    return (
      <Box className="flex h-full items-center justify-center">
        <Skeleton
          variant="rounded"
          animation="wave"
          width="100%"
          height="100%"
          data-testid="label-table-skeleton"
        />
      </Box>
    );
  }

  // Show empty or populated data grid
  return (
    <Box
      className="flex"
      height="calc(100%)"
      minHeight="200px"
      data-testid="label-table-container">
      {labelRows.length > 0 ? (
        <DataGrid
          data-testid="label-table-grid"
          headerHeight={40}
          initialState={{
            sorting: {
              sortModel: [{ field: "time", sort: "desc" }],
            },
          }}
          getRowId={(row: GridRowModel) => row?.id}
          rowHeight={40}
          rows={labelRows}
          columns={columns}
          sx={{
            borderRadius: "10px",
            // style to remove header separators
            ".MuiDataGrid-columnSeparator": {
              display: "none",
            },
            // style for content rows
            ".MuiDataGrid-row": {
              fontSize: "14px",
              lineHeight: "20px",
              backgroundColor: "#F2F5F6",
            },
            "& .MuiDataGrid-row:nth-child(2n)": {
              backgroundColor: "#FFFFFF",
            },
            // style for content cells
            ".MuiDataGrid-cell": {
              border: "none",
            },
            // Override the default height of the footer row
            '& .MuiDataGrid-footerContainer': {
              height: '40px',
              minHeight: '40px',
              justifyContent: "start"
            },
            "& .MuiTablePagination-toolbar": {
              minHeight: "40px",
              height: "40px",
            },
            "& .Mui-selected": {
              backgroundColor: "#CCEAFF !important",
            },
            // hide prev/next pagination buttons since there is always one page
            "& .MuiTablePagination-actions": {
              display: "none",
            },
          }}
          getRowClassName={(params) => (params.row.id === selectedLabelId ? "Mui-selected" : "")}
          selectionModel={selectedLabelId ? [selectedLabelId] : []}
          onRowClick={(params) => {
            onLabelSelected(params.row.id);
          }}
          disableColumnMenu
          rowsPerPageOptions={[]}
        />
      ) : (
        <Box
          className="flex items-center justify-center w-full text-gray-500"
          data-testid="label-table-empty"
        >
          No label data available
        </Box>
      )}
    </Box>
  );
}
